/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import _isEqual from 'lodash/isEqual'
import { getUserTypeFromToken, isAuthenticated } from '../_helpers/cookie'
import { generateOperatingWeekDays, generateSlots, getTimeInHours } from '../_helpers/facility'
import { History } from '../_helpers/history'
import { handleMessages } from '../_helpers/utilities'
import { INewBookingDetails } from '../_models/newBookingInterface'
import facilityService from '../_services/facilityServices'
import { RootState } from '../_store/store'
import { IFilters } from './../_models/commonInterface'

import {
  IAddLocation,
  ICheckDayFacilityConfigEvents,
  ICheckFacilityConfigEvents,
  IConnectFloorParams,
  ICreateFacilityBasicInfoDetails,
  ICreateFacilityConfiguration,
  IDayDetails,
  IFacilityState,
  IFacilityStatusDetails,
  IFloorParams,
  IGetBusinessDetails,
  IGetFacilityBookingCount,
  IUpdateFacilityBasicInfoDetails,
  IUpdateFacilityConfiguration,
  IUpdateFacilityConfigurationAndFloors,
  IUpdateFacilityDaySettings,
} from './../_models/facilityInterface'
import { startLoader, stopLoader } from './loaderSlice'
import {
  rescheduleBookingFromMail,
  setFacilityLoadingStatusFalse,
  setFacilityLoadingStatusTrue,
} from './newBookingSlice'
import { getPricingLimits } from './pricingSlice'
import { publicPagesState } from './publicPagesSlice'
import { errorToast, infoToast, successToast } from './toastSlice'

const initialState: IFacilityState = {
  facilities: {
    current_page: 1,
    data: [],
    total: 20,
    per_page: 10,
    last_page: 0,
  },
  bookingFacilities: [],
  filters: {
    page: 1,
    per_page: 10,
    // inactive not required for other slices.
    inactive: 1,
    search: '',
  },
  facilityStatus: {
    facilityId: '',
    status: false,
  },
  facilityBasicInfo: {
    facility_name: '',
    phone: '',
    country_code: '',
    iso_code: '',
    address: '',
    latitude: '',
    longitude: '',
    location: '',
    image: null,
    description: '',
  },
  facilityPhoneData: {
    contact_number: '',
    country_code: '',
    iso_code: '',
  },
  facilityLocation: {
    location_id: '',
    locationOptions: [],
    loading: false,
    initialLocationId: '',
  },
  facilityConfiguration: {
    facility_id: '',
    allow_cancelled_slot_booking: true,
    preferred_time_toggle: false,
    max_active_booking: '',
    max_bulk_booking: '',
    max_recurring_booking:
      '1' /* Default value set to 1 temporarily — to be updated once BE changes are implemented */,
    open_time: '',
    close_time: '',
    slot_interval: '',
    pre_buffer: '00:30',
    buffer: '00:30',
    slots: [],
    week_days: [],
    availability: '',
    recurring: false,
    requestsPerSlot: '1',
    isFacilityConfigurationCompleted: false,
    phone: '',
    country_code: '',
    iso_code: '',
    has_seat_selection: false,
    allow_recurring_booking: true,
    is_draft_mode: false,
    floors: [],
  },
  facilitySingleDayConfiguration: {
    unit: '',
    date: '',
    holiday: false,
    open_time: '',
    close_time: '',
    slot_interval: '00:30',
    slots: [],
    requestsPerSlot: '1',
  },
  facilityEditedEvents: [],
  facilityDrawer: false,
  facilityCurrentStep: 0,
  facilitySingleDayEditModal: false,
  isLoadingFacilityList: false,
  isFacilityBasicInfoCreated: false,
  mode: 'add',
  isLoadingBusinessFacilityList: false,
  isLoadingBusinessDetails: false,
  isInvalidBusiness: false,
  businessUrl: '',
  businessDetailsFilter: {
    page: 1,
    search: '',
  },
  connectedFloorDetails: [],
  warningModalState: false,
  availableFloorDetails: [],
  floorsConnected: false,
  bookingCount: {
    active: 0,
    blocked: 0,
  },
  connectFloorsParams: {
    facility_id: '',
    floors: [],
  },
  businessDetails: {
    business: {
      id_hash: '',
      name: null,
      description: null,
      address: null,
      url: null,
      phone: '',
      cover: null,
      logo: null,
      sms_enabled: 0,
      date_format: null,
      created_at: null,
      updated_at: null,
      deleted_at: null,
      user_id: 0,
      latitude: null,
      longitude: null,
      purchase_status: 0,
      restrictions_status: 0,
      profile_status: 0,
      location: null,
      country_code: '',
    },
    businessUnits: {
      current_page: 0,
      data: [],
      first_page_url: null,
      from: 0,
      last_page: 0,
      last_page_url: null,
      next_page_url: null,
      path: null,
      per_page: 0,
      prev_page_url: null,
      to: 0,
      total: 0,
    },
    unitsCount: 0,
  },
  completeFacilityList: [],
}

export const createFacilityBasicInfo = createAsyncThunk(
  '/createFacilityBasicInfo',
  async (
    facilityBasicInfoDetails: ICreateFacilityBasicInfoDetails,
    { dispatch, rejectWithValue },
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { updateStep, ...facilityBasicInfoData } = facilityBasicInfoDetails
    dispatch(startLoader())
    try {
      const response = await facilityService.createFacilityBasicInfo(facilityBasicInfoData)
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(getAllFacilities(initialState.filters))
      dispatch(getAllLocations())
      // update facility drawer status
      dispatch(updateFacilityBasicInfoCreationStatus(true))
      facilityBasicInfoDetails.updateStep()
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getAllLocations = createAsyncThunk(
  '/getAllLocations',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(updateLocationLoader(true))
    try {
      const response = await facilityService.getAllLocations()
      dispatch(updateLocationLoader(false))
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const addLocation = createAsyncThunk(
  '/addLocation',
  async (locationData: IAddLocation, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.addLocation(locationData)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const createFacilityConfiguration = createAsyncThunk(
  '/createFacilityConfiguration',
  async (
    facilityConfigurationDetails: ICreateFacilityConfiguration,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.createFacilityConfiguration(
        facilityConfigurationDetails,
      )
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(getAllFacilities(initialState.filters))
      if (facilityConfigurationDetails?.has_seat_selection === true) {
        dispatch(getFacilityDetails(facilityConfigurationDetails.facility_id))
        dispatch(updateFacilityCurrentStep(2))
      } else {
        dispatch(updateFacilityDrawerStatus(false))
        dispatch(updateFacilityCurrentStep(0))
        dispatch(resetFacilityBasicInfo())
        dispatch(resetFacilityConfiguration())
        dispatch(updateFacilityBasicInfoCreationStatus(false))
      }
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const updateFacilityConfiguration = createAsyncThunk(
  '/updateFacilityConfiguration',
  async (
    facilityConfigurationDetails: IUpdateFacilityConfiguration,
    { dispatch, rejectWithValue, getState },
  ) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.updateFacilityConfiguration(
        facilityConfigurationDetails,
      )
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      const { facility } = getState() as { facility: IFacilityState }
      dispatch(getAllFacilities(facility.filters))

      if (facilityConfigurationDetails?.has_seat_selection === true) {
        // If has_seat_selection is true, go to next tab (step 2)
        dispatch(getFacilityDetails(facilityConfigurationDetails.facility_id))
        // Set the step to 2 after getting facility details
        dispatch(updateFacilityCurrentStep(2))
      } else {
        // If has_seat_selection is false, close the drawer
        dispatch(updateFacilityDrawerStatus(false))
        dispatch(updateFacilityCurrentStep(0))
        dispatch(resetFacilityBasicInfo())
        dispatch(resetFacilityConfiguration())
        dispatch(updateFacilityBasicInfoCreationStatus(false))
      }
      // }

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const updateFacilityConfigurationAndFloors = createAsyncThunk(
  '/updateFacilityConfigurationAndFloors',
  async (
    facilityFloorDetails: IUpdateFacilityConfigurationAndFloors & {
      closeDrawer?: boolean
      toastType?: 'activateBooking'
      message?: string
    },

    { dispatch, rejectWithValue, getState },
  ) => {
    dispatch(startLoader())
    try {
      const { closeDrawer = true, toastType, message, ...facilityFloorData } = facilityFloorDetails
      const response = await facilityService.updateFacilityConfigurationAndFloors(facilityFloorData)
      const { facility } = getState() as { facility: IFacilityState }
      // Only call getFacilityDetails if we're not closing the drawer
      if (!closeDrawer) {
        dispatch(getFacilityDetails(facility.facilityConfiguration.facility_id))
        // Add conditional toasts based on toastType
        switch (toastType) {
          case 'activateBooking':
            dispatch(
              infoToast(
                handleMessages(
                  message ??
                    'Request per slot settings have been updated. Facility is active for booking.',
                ),
              ),
            )
            break
          default:
            // No toast for manual updates
            break
        }
      }
      dispatch(stopLoader())
      if (closeDrawer) {
        dispatch(successToast(handleMessages(response.message)))

        dispatch(getAllFacilities(facility.filters))

        dispatch(updateFacilityDrawerStatus(false))
        dispatch(updateFacilityCurrentStep(0))
        dispatch(resetFacilityBasicInfo())
        dispatch(resetFacilityConfiguration())
        dispatch(updateFacilityBasicInfoCreationStatus(false))
      }

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const updateFacilityDaySettings = createAsyncThunk(
  '/updateFacilityDaySettings',
  async (facilityDaySettingsDetails: IUpdateFacilityDaySettings, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.updateFacilityDaySettings(facilityDaySettingsDetails)
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(getFacilityDetails(facilityDaySettingsDetails.unit))
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getAllFacilities = createAsyncThunk(
  '/getAllFacilities',
  async (filters: IFilters, { dispatch, rejectWithValue, getState }) => {
    try {
      const response = await facilityService.getAllFacilities(filters)
      const { isSubscriptionBased } = publicPagesState(getState() as RootState)
      if (isSubscriptionBased) {
        dispatch(getPricingLimits({}))
      }
      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getLocationFacilities = createAsyncThunk(
  '/getLocationFacilities',
  async (location: number, { dispatch, rejectWithValue }) => {
    dispatch(setFacilityLoadingStatusTrue())

    try {
      const params = {
        location: location,
        inactive: 0,
        all_facilities: 1,
      }
      const response = await facilityService.getLocationFacilities(params)
      dispatch(setFacilityLoadingStatusFalse())
      return response
    } catch (err: any) {
      dispatch(setFacilityLoadingStatusFalse())

      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getFacilityDetails = createAsyncThunk(
  '/getFacilityDetails',
  async (facilityId: string, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getFacilityDetails(facilityId)
      dispatch(getAllLocations())
      dispatch(stopLoader())
      dispatch(updateFacilityDrawerStatus(true))

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getFacilityBookingCount = createAsyncThunk(
  '/getFacilityBookingCount',
  async (facilityDetails: IGetFacilityBookingCount, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getFacilityBookingCount(facilityDetails)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const updateFacilityBasicInfo = createAsyncThunk(
  '/updateFacilityBasicInfo',
  async (
    facilityBasicInfoDetails: IUpdateFacilityBasicInfoDetails,
    { dispatch, rejectWithValue, getState },
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { updateStep, ...facilityBasicInfoData } = facilityBasicInfoDetails
    dispatch(startLoader())
    try {
      const response = await facilityService.updateFacilityBasicInfo(facilityBasicInfoData)
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      dispatch(getFacilityDetails(facilityBasicInfoDetails.facility_id))
      const { facility } = getState() as { facility: IFacilityState }

      dispatch(getAllFacilities(facility.filters))
      dispatch(getAllLocations())
      facilityBasicInfoDetails.updateStep()

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const getFacilityDayConfiguration = createAsyncThunk(
  '/getFacilityDayConfiguration',
  async (dayDetails: IDayDetails, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getFacilityDayConfiguration(dayDetails)
      dispatch(stopLoader())
      dispatch(updateFacilitySingleDayEditModalStatus(true))
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const getConnectedFloors = createAsyncThunk(
  '/getConnectedFloors',
  async (facilityId: string, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getConnectedFloors(facilityId)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const getAvailableFloors = createAsyncThunk(
  '/getAvailableFloors',
  async (facilityId: string, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getAvailableFloors(facilityId)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const connectFloorToFacility = createAsyncThunk(
  '/connectFloorToFacility',
  async (params: IConnectFloorParams, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoader())
    try {
      const { facility } = getState() as { facility: IFacilityState }
      const response = await facilityService.connectFloorToFacility(params)
      const facilityId = params.facility_id
      dispatch(getAllFacilities(facility.filters)) // Inorder to update draft state in facility card
      dispatch(stopLoader())
      // dispatch(successToast(handleMessages(response.message)))  // Temporarily hiding the success toast until design is finalized.
      dispatch(getConnectedFloors(facilityId))
      if (params.unlink) {
        // This is a disconnection
        dispatch(infoToast(handleMessages('Request per slot settings have been updated.')))
      } else {
        // This is a connection
        dispatch(infoToast(handleMessages('Request per slot settings can be edited.')))
      }

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const checkActiveBookingsOnFloor = createAsyncThunk(
  '/checkActiveBookingsOnFloor',
  async (floors: IFloorParams, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.checkActiveBookingsOnFloor(floors)

      dispatch(stopLoader())
      const { active_booking_count, temp_seat_booking_count } = response.data.data
      const state = getState() as RootState
      const facilityState = state.facility
      const previousConnectedFloorIds = facilityState.availableFloorDetails
        ?.filter((floor) => floor?.connected === 1)
        .map((floor) => floor?.id)

      const currentSelectedFloorIds = facilityState.connectFloorsParams?.floors || []

      const isDisconnectingLastConnectedFloor =
        (facilityState.connectedFloorDetails.length === 1 &&
          facilityState?.connectFloorsParams.unlink === true) ||
        (previousConnectedFloorIds?.length === 1 &&
          currentSelectedFloorIds.length === 0 &&
          previousConnectedFloorIds[0] &&
          !currentSelectedFloorIds.includes(previousConnectedFloorIds[0]))
      if (
        active_booking_count > 0 ||
        temp_seat_booking_count > 0 ||
        isDisconnectingLastConnectedFloor
      ) {
        dispatch(openWarningModal())
        return response
      } else {
        const connectFloorsParams = facilityState?.connectFloorsParams
        dispatch(connectFloorToFacility(connectFloorsParams))
        dispatch(setFloorsConnected(true))
      }

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const checkFacilityConfigurationEvents = createAsyncThunk(
  '/checkFacilityConfigEvents',
  async (
    facilityConfigurationDetails: ICheckFacilityConfigEvents | ICheckDayFacilityConfigEvents,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.checkFacilityConfigurationEvents(
        facilityConfigurationDetails,
      )
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      return rejectWithValue(err)
    }
  },
)

export const deleteFacility = createAsyncThunk(
  '/deleteFacility',
  async (facilityId: string, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.deleteFacility(facilityId)
      dispatch(stopLoader())
      dispatch(successToast(handleMessages(response.message)))
      const { facility } = getState() as { facility: IFacilityState }
      dispatch(getAllFacilities(facility.filters))
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const updateFacilityStatus = createAsyncThunk(
  '/updateStatus',
  async (facilityStatus: IFacilityStatusDetails, { dispatch, rejectWithValue, getState }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.updateFacilityStatus(facilityStatus)
      dispatch(successToast(handleMessages(response.message)))
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      const { facility } = getState() as { facility: IFacilityState }
      dispatch(getAllFacilities(facility.filters))
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

// ### customer service ###

export const getFacilityListByBusinessUrl = createAsyncThunk(
  '/getFacilityListByBusinessUrl',
  async (filter: IGetBusinessDetails, { dispatch, rejectWithValue, getState }) => {
    try {
      const response = await facilityService.getFacilityListByBusinessUrl(filter)

      // For handling reschedule from mail flow
      const searchParams = new URLSearchParams(window.location.search)
      const isRescheduleLink = searchParams.get('reschedule') ? true : false
      const bookingToken = searchParams.get('token')
      const linkExpiryTime = searchParams.get('expires')
      const loggedInUserType = getUserTypeFromToken()
      const { newBooking } = getState() as {
        newBooking: INewBookingDetails
      }

      if (
        response &&
        response.data.business.restrictions_status === 0 &&
        bookingToken &&
        linkExpiryTime
      ) {
        if (isAuthenticated() && loggedInUserType && loggedInUserType === 'customer') {
          History.navigate(`/customer/my-bookings${window.location.search}`)
        } else {
          if (isRescheduleLink) {
            dispatch(
              rescheduleBookingFromMail({
                token: bookingToken,
                expires: linkExpiryTime,
                bookingDetails: newBooking.rescheduleBookingDetails,
              }),
            )
          }
          // handle guest rescheduling flow here
        }
      }

      return response
    } catch (err: any) {
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const facilitySlice = createSlice({
  name: 'facility',
  initialState,
  reducers: {
    changeFilters: (state, action) => {
      return {
        ...state,
        filters: action.payload,
      }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    },
    // To reset the values in Filter
    updateLocationLoader: (state, action) => {
      return {
        ...state,
        facilityLocation: {
          ...state.facilityLocation,
          loading: action.payload,
        },
      }
    },
    updateLocationId: (state, action) => {
      return {
        ...state,
        facilityLocation: {
          ...state.facilityLocation,
          location_id: action.payload,
        },
      }
    },
    updateFacilityId: (state, action) => {
      return {
        ...state,
        facilityConfiguration: {
          ...state.facilityConfiguration,
          facility_id: action.payload,
        },
      }
    },
    updateFacilityMode: (state, action) => {
      return {
        ...state,
        mode: action.payload,
      }
    },
    updateFacilityDrawerStatus: (state, action) => {
      return {
        ...state,
        facilityDrawer: action.payload,
      }
    },
    updateFacilitySingleDayEditModalStatus: (state, action) => {
      return {
        ...state,
        facilitySingleDayEditModal: action.payload,
      }
    },
    updateFacilityCurrentStep: (state, action) => {
      return {
        ...state,
        facilityCurrentStep: action.payload,
      }
    },
    resetFacilityBasicInfo: (state) => {
      return {
        ...state,
        facilityBasicInfo: initialState.facilityBasicInfo,
        facilityPhoneData: initialState.facilityPhoneData,
        facilityLocation: {
          ...state.facilityLocation,
          location_id: initialState.facilityLocation.location_id,
          loading: initialState.facilityLocation.loading,
        },
        mode: initialState.mode,
      }
    },
    resetFacilityConfiguration: (state) => {
      return {
        ...state,
        facilityConfiguration: initialState.facilityConfiguration,
        mode: initialState.mode,
      }
    },
    resetFacilityDropdown: (state) => {
      state.bookingFacilities = []
    },
    updateFacilityBasicInfoCreationStatus: (state, action) => {
      return {
        ...state,
        isFacilityBasicInfoCreated: action.payload,
      }
    },
    openWarningModal: (state) => {
      state.warningModalState = true
    },
    closeWarningModal: (state) => {
      state.warningModalState = false
    },
    setConnectFloorsParams: (state, action) => {
      state.connectFloorsParams = action.payload
    },
    // ### customer related reducer ###
    changeBusinessUrl: (state, action) => {
      return {
        ...state,
        businessUrl: action.payload,
      }
    },
    changeBusinessDetailsFilter: (state, action) => {
      return {
        ...state,
        businessDetailsFilter: action.payload,
      }
    },
    resetBusinessDetailsFilter: (state) => {
      state.businessDetailsFilter = initialState.businessDetailsFilter
    },
    resetFacilityListData: (state) => {
      state.completeFacilityList = initialState.completeFacilityList
    },
    resetBusinessDetailsData: (state) => {
      state.businessDetails = initialState.businessDetails
      state.businessDetailsFilter = initialState.businessDetailsFilter
      state.isLoadingBusinessFacilityList = initialState.isLoadingBusinessFacilityList
      state.completeFacilityList = initialState.completeFacilityList
      state.businessUrl = initialState.businessUrl
    },
    setFloorsConnected: (state, action) => {
      state.floorsConnected = action.payload
    },
    resetFloorsConnected: (state) => {
      state.floorsConnected = false
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getAllFacilities.pending, (state) => {
        state.isLoadingFacilityList = true
      })
      .addCase(getAllFacilities.fulfilled, (state, action) => {
        state.facilities = action.payload.data.facilities
        state.isLoadingFacilityList = false
      })
      .addCase(getAllFacilities.rejected, (state) => {
        state.isLoadingFacilityList = false
      })

      .addCase(createFacilityBasicInfo.fulfilled, (state, action) => {
        state.facilityBasicInfo.facility_name = action.payload.data.facility.name
        state.facilityBasicInfo.address = action.payload.data.facility.address
        state.facilityBasicInfo.latitude = action.payload.data.facility.latitude
        state.facilityBasicInfo.longitude = action.payload.data.facility.longitude
        state.facilityBasicInfo.location = action.payload.data.facility.map_location
        state.facilityBasicInfo.image = action.payload.data.facility.image
        state.facilityBasicInfo.description = action.payload.data.facility.description
          ? action.payload.data.facility.description
          : ''

        state.facilityLocation.initialLocationId =
          action.payload.data.facility.location_id.toString()
        state.facilityLocation.location_id = action.payload.data.facility.location_id

        state.facilityConfiguration.facility_id = action.payload.data.facility.id_hash

        state.facilityPhoneData.contact_number = action.payload.data.facility?.contact_number
        state.facilityPhoneData.country_code = action.payload.data.facility?.country_code
        state.facilityPhoneData.iso_code = action.payload.data.facility?.iso_code
      })
      .addCase(getAllLocations.fulfilled, (state, action) => {
        state.facilityLocation.locationOptions = action.payload.data.locations
      })
      .addCase(addLocation.fulfilled, (state, action) => {
        state.facilityLocation.location_id = action.payload.data.location.id
      })
      .addCase(getLocationFacilities.fulfilled, (state, action) => {
        state.bookingFacilities = action.payload.data.facilities
      })
      .addCase(getFacilityDetails.fulfilled, (state, action) => {
        state.facilityBasicInfo.facility_name = action.payload.data.facility.name
        state.facilityBasicInfo.address = action.payload.data.facility.address
        state.facilityBasicInfo.latitude = action.payload.data.facility.latitude
        state.facilityBasicInfo.longitude = action.payload.data.facility.longitude
        state.facilityBasicInfo.location = action.payload.data.facility.map_location
        state.facilityBasicInfo.image = action.payload.data.facility.image
        state.facilityBasicInfo.description = action.payload.data.facility.description
          ? action.payload.data.facility.description
          : ''

        state.facilityPhoneData.contact_number = action.payload.data.facility?.contact_number
        state.facilityPhoneData.country_code = action.payload.data.facility?.country_code
        state.facilityPhoneData.iso_code = action.payload.data.facility?.iso_code

        state.facilityLocation.initialLocationId =
          action.payload.data.facility.location_id.toString()
        state.facilityLocation.location_id = action.payload.data.facility.location_id.toString()

        state.facilityConfiguration.allow_cancelled_slot_booking =
          action.payload.data.facility.allow_cancelled_slot_boooking

        state.facilityConfiguration.preferred_time_toggle =
          action.payload.data.facility.preferred_time_toggle
        state.facilityConfiguration.is_draft_mode = action.payload.data.facility.is_draft_mode

        state.facilityConfiguration.has_seat_selection =
          action.payload.data.facility.has_seat_selection

        state.facilityConfiguration.max_active_booking = action.payload.data.facility
          .max_active_booking
          ? action.payload.data.facility.max_active_booking
          : ''
        state.facilityConfiguration.max_bulk_booking = action.payload.data.facility.max_bulk_booking
          ? action.payload.data.facility.max_bulk_booking
          : ''
        state.facilityConfiguration.max_recurring_booking =
          action.payload.data.facility.max_recurring_booking > 0
            ? action.payload.data.facility.max_recurring_booking
            : 1
        state.facilityConfiguration.allow_recurring_booking =
          action.payload.data.facility.allow_recurring_booking
        state.facilityConfiguration.open_time = action.payload.data.facility.open_time
        state.facilityConfiguration.close_time = action.payload.data.facility.close_time
        state.facilityConfiguration.close_time = action.payload.data.facility.close_time
        state.facilityConfiguration.slot_interval = getTimeInHours(action.payload.data.interval)
        state.facilityConfiguration.pre_buffer = getTimeInHours(action.payload.data.pre_buffer)
        state.facilityConfiguration.buffer = getTimeInHours(action.payload.data.buffer)
        ;(state.facilityConfiguration.slots = action.payload.data.base_slots
          ? generateSlots(
              action.payload.data.facility.open_time,
              action.payload.data.interval,
              'minutes',
              action.payload.data.facility.close_time,
              '10',
              action.payload.data.base_slots.slot_seats,
            )
          : []),
          (state.facilityConfiguration.week_days = generateOperatingWeekDays(
            action.payload.data.facility.open_time,
            action.payload.data.facility.close_time,
            action.payload.data.days,
          ))

        state.facilityConfiguration.availability = action.payload.data.availability
        state.facilityConfiguration.recurring = action.payload.data.facility.recurring
        state.facilityEditedEvents = action.payload.data.events_edited
        state.facilityConfiguration.isFacilityConfigurationCompleted = action.payload.data
          .base_slots
          ? true
          : false
        state.facilityConfiguration.floors = action.payload.data.facility.floors
      })
      .addCase(getFacilityDayConfiguration.fulfilled, (state, action) => {
        state.facilitySingleDayConfiguration.unit = action.payload.data.unit
        state.facilitySingleDayConfiguration.date = action.payload.data.start
        state.facilitySingleDayConfiguration.open_time = action.payload.data.start_time
        state.facilitySingleDayConfiguration.close_time = action.payload.data.end_time
        state.facilitySingleDayConfiguration.holiday =
          action.payload.data.holiday === 'Yes' ? true : false
        state.facilitySingleDayConfiguration.slot_interval = getTimeInHours(
          action.payload.data.interval_mins,
        )

        state.facilitySingleDayConfiguration.slots = generateSlots(
          action.payload.data.start_time,
          action.payload.data.interval_mins,
          'minutes',
          action.payload.data.end_time,
          '10',
          action.payload.data.slot_seats ? action.payload.data.slot_seats : '',
        )
      })
      .addCase(getConnectedFloors.fulfilled, (state, action) => {
        state.connectedFloorDetails = action.payload?.data?.connected_floors
      })
      .addCase(getAvailableFloors.fulfilled, (state, action) => {
        state.availableFloorDetails = action.payload?.data?.available_floors
      })
      .addCase(connectFloorToFacility.fulfilled, (state) => {
        state.warningModalState = false
        state.floorsConnected = true
      })
      .addCase(checkActiveBookingsOnFloor.pending, (state) => {
        state.warningModalState = false
      })
      .addCase(checkActiveBookingsOnFloor.fulfilled, (state, action) => {
        state.bookingCount.active = action.payload?.data?.data?.active_booking_count
        state.bookingCount.blocked = action.payload?.data?.data?.temp_seat_booking_count
      })

      .addCase(getFacilityListByBusinessUrl.pending, (state) => {
        state.isLoadingBusinessFacilityList = true
        state.isInvalidBusiness = false
      })
      .addCase(getFacilityListByBusinessUrl.fulfilled, (state, action) => {
        state.businessDetails = action.payload.data
        if (
          action.payload.data?.businessUnits?.data &&
          !_isEqual(state.completeFacilityList, action.payload.data?.businessUnits?.data)
        ) {
          // Append action.payload.data to existing completeFacilityList
          state.completeFacilityList = [
            ...state.completeFacilityList,
            ...(action.payload.data?.businessUnits?.data || []),
          ]
        }
        state.isLoadingBusinessFacilityList = false
        state.isInvalidBusiness = false
      })
      .addCase(getFacilityListByBusinessUrl.rejected, (state) => {
        state.isLoadingBusinessFacilityList = false
        state.isInvalidBusiness = true
      })
  },
})

export const {
  changeFilters,
  updateLocationId,
  updateLocationLoader,
  updateFacilityId,
  updateFacilityMode,
  resetFacilityBasicInfo,
  updateFacilityDrawerStatus,
  updateFacilitySingleDayEditModalStatus,
  updateFacilityCurrentStep,
  resetFacilityConfiguration,
  resetFacilityDropdown,
  updateFacilityBasicInfoCreationStatus,
  resetFilters,
  openWarningModal,
  closeWarningModal,
  changeBusinessDetailsFilter,
  resetBusinessDetailsFilter,
  changeBusinessUrl,
  resetFacilityListData,
  resetBusinessDetailsData,
  setConnectFloorsParams,
  setFloorsConnected,
  resetFloorsConnected,
} = facilitySlice.actions
export const facilityState = (state: RootState) => state.facility
export default facilitySlice.reducer
